import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { API_ENDPOINT } from "../../utils/constants";

export default function CitySelector({
  countryCode,
  onChange,
  required,
  selectedCity,
  stateCode,
}) {
  const abortControllerRef = useRef(null);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      if (!countryCode || !stateCode) {
        setCities([]);
        return;
      }

      // Cancel previous request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        if (mountedRef.current) setLoading(true);

        const response = await fetch(
          `${API_ENDPOINT}/visitor/get-cities/${countryCode}/${stateCode}`,
          { signal: controller.signal }
        );
        const data = await response.json();

        if (!controller.signal.aborted && mountedRef.current) {
          // Map and sort alphabetically by name
          const cityData = data
            .map((city) => ({ name: city.name }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setCities(cityData);
        }
      } catch (error) {
        if (error.name !== "AbortError" && mountedRef.current) {
          console.error("Error fetching cities:", error);
          setCities([]);
        }
      } finally {
        if (!controller.signal.aborted && mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchCities();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [countryCode, stateCode]);

  const handleChange = (event, value) => {
    onChange?.(value);
  };

  return (
    <Autocomplete
      disabled={!countryCode || !stateCode || loading}
      getOptionLabel={(option) => (option ? option.name : "")}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      loading={loading}
      noOptionsText={
        !countryCode
          ? "Select country first"
          : !stateCode
          ? "Select state first"
          : "No cities available"
      }
      onChange={handleChange}
      options={cities}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          label="Select City"
          required={required}
          variant="outlined"
        />
      )}
      size="small"
      value={selectedCity || null}
    />
  );
}
